/**
 * KPI ページの全 bar chart で共有する見た目プリセット。
 *  - Bar 幅は recharts のオートスケールではなく `BAR_MAX_SIZE` で上限指定し、
 *    どのチャートでも 1 本のバーが太くなりすぎないようにする
 *  - Bar 角は `[3, 3, 0, 0]` のラウンド (top のみ) でメンバー管理ページの
 *    BudgetBarChart と揃える。stacked bar では「stack の最上段」にだけ radius を
 *    付け、中間セグメントは段差が出ないよう 0 を返す
 *  - Legend は recharts デフォルトのアイコンが大きく角張って見えるため、
 *    rounded-square のアイコン + 小さいフォントの自前 content で描画する
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business KPI ページ全 bar chart 共通の見た目プリセット (bar 幅・角丸・凡例) を提供
 * @graph-connects none
 */
import type { ReactNode } from 'react';
import type { DefaultLegendContentProps, LegendPayload } from 'recharts';
import type { KpiWeeklyTrendBreakdownKey } from '../../../types/api.js';

/** Bar 1 本の最大幅 (px)。recharts はカテゴリ幅に合わせて自動縮小するため上限のみ指定 @graph-connects none */
export const BAR_MAX_SIZE = 30;

/** stacked bar の最上段 / 単段 bar 用の角丸プリセット (top 2 角のみ) @graph-connects none */
export const BAR_RADIUS_TOP: [number, number, number, number] = [3, 3, 0, 0];

/** stacked bar の中間セグメント用 @graph-connects none */
export const BAR_RADIUS_NONE: [number, number, number, number] = [0, 0, 0, 0];

/**
 * stacked bar の i 番目に渡す radius を返す。idx が stack の最後 (= 描画上の最上段) の
 * ときだけ `BAR_RADIUS_TOP` を返し、それ以外は 0 を返す。
 *  - 末尾セグメントが 0 値で実際は途中のセグメントが頂点になっている月は段差が出るが、
 *    全段に radius を付ける「階段状」よりは破綻が少ないため最上段のみで運用する
 * @graph-connects none
 */
export function stackedBarRadius(index: number, total: number): [number, number, number, number] {
  return index === total - 1 ? BAR_RADIUS_TOP : BAR_RADIUS_NONE;
}

/**
 * チーム内訳 stack の安定表示順 (Weekly / Monthly chart で共用)。
 *  - scope='all' (data=[pdg, ace]) では ACE → PDG の順で表示するため 'ace' を 'pdg' の前
 *  - ACE 配下は user 指定の airCloset → Mall → Platform → QA 順
 *  - PDG 配下は PDG → TE → SRE
 * `KpiMonthlyTrendBreakdownKey = KpiWeeklyTrendBreakdownKey` なので両 chart で
 * そのままインポートして使える
 * @graph-connects none
 */
export const TEAM_BREAKDOWN_ORDER: readonly KpiWeeklyTrendBreakdownKey[] = [
  'ace',
  'aircloset',
  'mall',
  'platform',
  'qa',
  'pdg',
  'tet',
  'sre',
];

/**
 * 角丸正方形アイコン + 小さい label の凡例 1 項目。
 *  - Person-month のような Line シリーズは type='line' で渡され、丸ドット + 直線で表現
 *  - 通常の Bar シリーズは rounded-square のアイコン
 * モジュール内 private な React コンポーネント (createCompactLegendContent からのみ呼ばれる)
 * @graph-connects none
 */
function LegendIcon({ color, type }: { color: string; type?: LegendPayload['type'] }) {
  if (type === 'line' || type === 'plainline') {
    return (
      <svg width="16" height="10" aria-hidden="true" focusable="false">
        <line x1="0" y1="5" x2="16" y2="5" stroke={color} strokeWidth={2} />
        <circle cx="8" cy="5" r="2.4" fill={color} />
      </svg>
    );
  }
  return (
    <span
      aria-hidden="true"
      className="inline-block h-2.5 w-2.5 rounded-[3px]"
      style={{ backgroundColor: color }}
    />
  );
}

/**
 * recharts Legend の `content` プロップにそのまま渡せるカスタムレンダラを生成する。
 * `labelFormatter` で各項目のラベル文字列を呼び出し側のロジック (i18n / breakdown 解決)
 * に委譲する。
 *  - iconSize / wrapperStyle / formatter を個別にイジるより、payload を直接受けて
 *    レイアウトごと組み直すほうが「アイコンの形・サイズ・フォントサイズ」を完全に
 *    コントロールできる
 *  - recharts の Legend.content 型は `ReactElement | ((props) => ReactNode)`。
 *    関数形式で受けるため戻り値を `(props: DefaultLegendContentProps) => ReactNode` に固定する
 * @graph-connects none
 */
export function createCompactLegendContent(
  labelFormatter: (value: string) => ReactNode,
  options?: {
    /** 凡例に出さない dataKey/value を絞り込む。true を返した要素だけ描画する */
    filter?: (value: string) => boolean;
  },
): (props: DefaultLegendContentProps) => ReactNode {
  const filter = options?.filter;
  return function CompactLegendContent({ payload }) {
    if (!payload || payload.length === 0) return null;
    const filtered = filter ? payload.filter((entry) => filter(entry.value ?? '')) : payload;
    if (filtered.length === 0) return null;
    return (
      <ul className="m-0 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 p-0 pt-1 text-[11px] leading-[15px] text-[var(--color-text-secondary)] list-none">
        {filtered.map((entry, i) => {
          const value = entry.value ?? '';
          return (
            <li key={`${value}-${String(i)}`} className="inline-flex items-center gap-1">
              <LegendIcon color={entry.color ?? 'var(--color-text-muted)'} type={entry.type} />
              <span>{labelFormatter(value)}</span>
            </li>
          );
        })}
      </ul>
    );
  };
}
